import { APIError, useApplicantAPI, useSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import {
  BrandProvider,
  Container,
  Layout,
  Loader,
  showToast,
} from "@certn/cascadia-react";
import { changeLanguage } from "i18next";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { ApplyLinkEmailForm } from "./components/ApplyLinkEmailForm";
import { AppFooter } from "../../components/app-footer";
import { AppHeader } from "../../components/app-header";
import { NotFoundError } from "../../components/not-found-error/NotFoundError";

export function ApplyLinkPage() {
  const { t } = useTranslation();
  const { applyLinkId } = useParams();
  const navigate = useNavigate();
  const navigateToSignInPage = () => navigate("/");

  const { data, isLoading } = useApplicantAPI(
    `/api/internal/applicant/apply-link/${applyLinkId}/` as "/api/internal/applicant/apply-link/{id}/"
  );

  const { submit, isSubmitting } = useSubmit(
    `/api/internal/applicant/case/apply-link/create/`,
    { throwErrors: true }
  );

  // Use the language configured on the apply link, fall back to browser language
  const language = data?.applicant_language || navigator.language || "en-CA";

  useEffect(() => {
    changeLanguage(language);
  }, [language]);

  const submitEmail = async ({ email }: { email: string }) => {
    const body = {
      apply_link: applyLinkId,
      email_address: email,
    };

    try {
      const response = await submit(body);
      // Navigate with subject_id if provided in response (for cobranding)
      const subjectId = (response as { subject_id?: string })?.subject_id;
      if (subjectId) {
        // Use "/" instead of "/ca" to let RegionProvider redirect to the user's region
        navigate(`/?subject_id=${encodeURIComponent(subjectId)}`);
      } else {
        navigateToSignInPage();
      }
      showToast(t("Success. Email Sent"));
    } catch (error) {
      if (
        error instanceof APIError &&
        error.status === 400 &&
        error.detail === "Case exists for this email address and apply link"
      ) {
        navigate(`/apply-link/already-registered?email=${email}`);
        return;
      }
      showToast(t("Something went wrong"), { type: "error" });
    }
  };

  const primaryColor = data?.co_branding?.org_primary_color || undefined;

  return (
    <BrandProvider primaryColor={primaryColor}>
      <Layout
        layout="applicant/narrow"
        header={
          <AppHeader
            hideSettings
            showLoginButton
            branding={data?.co_branding ?? undefined}
          />
        }
        footer={<AppFooter />}
        backgroundColor={data?.is_active ? "surface-base" : "surface-sunken"}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <ApplyLinkPageContent
            applyLink={data}
            submitEmail={submitEmail}
            isSubmittingToAPI={isSubmitting}
          />
        )}
      </Layout>
    </BrandProvider>
  );
}

interface ApplyLinkPageContentProps {
  applyLink: components["schemas"]["ApplyLinkDetails"] | undefined;
  submitEmail: (values: { email: string }) => Promise<void>;
  isSubmittingToAPI: boolean;
}

function ApplyLinkPageContent({
  applyLink,
  submitEmail,
  isSubmittingToAPI,
}: ApplyLinkPageContentProps) {
  const { t } = useTranslation();
  const group = applyLink?.customer_group?.applicant_facing_name;

  if (!applyLink) {
    return (
      <NotFoundError
        title={t("This link is not valid")}
        message={t(
          "The URL for this link is not valid. Please ensure that you’ve copy and pasted the correct link into the browser search bar."
        )}
      />
    );
  }

  if (!applyLink.is_active) {
    return (
      <NotFoundError
        title={t("This link is no longer valid")}
        message={t("The application link for {{group}} is no longer valid.", {
          group,
        })}
      />
    );
  }

  if (!applyLink.is_valid_billing_profile) {
    return (
      <NotFoundError
        title={t("This link is not valid")}
        message={t("The application link is not valid.")}
      />
    );
  }

  if (!applyLink.is_valid_payment_profile) {
    return (
      <NotFoundError
        title={t("This link is not valid")}
        message={t("The application link is not valid.")}
      />
    );
  }

  return (
    <Container>
      <ApplyLinkEmailForm
        submitEmail={submitEmail}
        checks={applyLink?.checks}
        credentials={applyLink?.credentials}
        customerGroup={group}
        message={applyLink?.additional_message}
        isSubmittingToAPI={isSubmittingToAPI}
      />
    </Container>
  );
}
